/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useEffect,
  useState,
  KeyboardEvent,
  ChangeEvent,
  useCallback,
  useRef,
} from "react";
import Layout from "../components/Layout";
import PrintSettings from "../components/PrintSetting";
import {
  fetchProductDetails,
  fetchPriceCategories,
  getSerialNumbers,
  getPrintSettings,
  saveBarcode,
  savePrintSettings,
} from "../services/api";
import { toast } from "react-toastify";
import GroupedDetailProduct from "../components/GroupedDetailProduct";
import PrintPreview from "../components/PrintPreview";
import { logout } from "../utils/Auth";
import { useNavigate } from "react-router-dom";
import {
  getProductBarcode,
  Product,
  PriceCategory,
  ProductGroup,
  SerialNumberItem,
} from "../types/product";
import { PrintSettingsStateTypes } from "../types/print_settings";
import ModalGenerateBarcode from "../components/ModalGenerateBarcode";
import SerialNumberModal from "../components/SerialNumberModal";

const getInitialPrintSettings = (): PrintSettingsStateTypes => {
  return {
    showName: true,
    showPrice: true,
    showCode: true,
    showBarcode: true,
    showDate: true,
    showDisplay: false,
    showPriceDisplay: true,
    showCodeDisplay: true,
    showNameDisplay: true,
    showDateDisplay: true,
    barcodeWidth: 2,
    barcodeHeight: 60,
    pageWidth: 15,
    pageHeight: 5,
    fontSize: 16,
    fontPrice: 16,
    marginTop: 3,
    marginBottom: 0,
    marginLeft: 10,
    marginRight: 10,
    barcodeType: "CODE128",
    printPerLine: 2,
    displayQuantity: 1,
    printQuantity: 1,
    printLine: "2",
  };
};

const Home: React.FC = () => {
  const [query, setQuery] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalGenerate, setModalGenerate] = useState<boolean>(false);
  const [product, setProduct] = useState<Product | null>(null);
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [printSettings, setPrintSettings] = useState<PrintSettingsStateTypes>(
    getInitialPrintSettings(),
  );

  const [suggestions, setSuggestions] = useState<Product[]>([]);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [priceCategories, setPriceCategories] = useState<PriceCategory[]>([]);
  const [selectedPriceCategory, setSelectedPriceCategory] = useState("");
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const serialTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [serialModalItem, setSerialModalItem] = useState<Product | null>(null);
  const [serialNumbers, setSerialNumbers] = useState<SerialNumberItem[]>([]);
  const [serialKeyword, setSerialKeyword] = useState("");
  const [selectedSerialNumbers, setSelectedSerialNumbers] = useState<string[]>(
    [],
  );
  const [isSerialLoading, setIsSerialLoading] = useState(false);
  const [serialError, setSerialError] = useState<string | null>(null);

  const navigate = useNavigate();

  const fetchPrintSettings = useCallback(async () => {
    try {
      const data = await getPrintSettings();
      if (data?.data?.printSettings) {
        const parsed = JSON.parse(data.data.printSettings);
        setPrintSettings(parsed?.payloadPrintSetting);
      }
    } catch (err) {
      console.error("Failed fetch print settings", err);
    }
  }, []);

  useEffect(() => {
    fetchPrintSettings();
  }, []);

  useEffect(() => {
    fetchPriceCategories()
      .then((response) => setPriceCategories(response.data))
      .catch((err: any) => {
        toast.error(err.message || "Failed to fetch price categories", {
          position: "top-right",
        });
      });
  }, []);

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    try {
      const { name, type, value, checked } = e.target;
      const newPrintSettings = {
        ...printSettings,
        [name]: type === "checkbox" ? checked : value,
      };
      setPrintSettings(newPrintSettings);
      await savePrintSettings(newPrintSettings);
    } catch (err: any) {
      toast.error(err.message || "Failed to save print settings");
    }
  };

  const handleSearch = async (searchTerm?: string) => {
    const term = (searchTerm ?? query).trim();
    if (!term) return;
    setIsLoading(true);
    try {
      const res = await fetchProductDetails(term, selectedPriceCategory || undefined);
      if (res?.data) {
        setSuggestions(
          Array.isArray(res.data?.items) ? res.data?.items : [res.data?.items],
        );
        setShowSuggestions(true);
      } else {
        toast.error("Data not found", { position: "top-right" });
        setSuggestions([]);
      }
    } catch (err: any) {
      toast.error(err.message || "Data not found", { position: "top-right" });
      if (err.message?.includes("authentication")) {
        logout();
        navigate("/login");
        toast.info("Anda telah logout", { position: "top-right" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      if (val.trim()) {
        handleSearch(val);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (serialTimerRef.current) {
        clearTimeout(serialTimerRef.current);
      }
    };
  }, []);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && query.trim()) {
      handleSearch();
    }
  };

  const handleSelectSuggestion = (product: Product) => {
    const exists = productGroups.some(
      (group) => group.item.itemNumber === product.itemNumber,
    );
    if (!exists) {
      setProductGroups((prev) => [
        ...prev,
        {
          item: product,
          children: product.manageSN === false ? [{ ...product, serialNumber: null }] : [],
        },
      ]);
      setProduct(product);
    } else {
      toast.error("Product already added!", { position: "top-right" });
    }
    setQuery("");
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleRemoveChild = (itemNumber: string, childIndex: number) => {
    setProductGroups((groups) =>
      groups.flatMap((group) => {
        if (group.item.itemNumber !== itemNumber) return [group];

        const children = group.children.filter((_, index) => index !== childIndex);
        return children.length ? [{ ...group, children }] : [];
      }),
    );
  };

  const handleAddUpc = (item: Product) => {
    setProductGroups((groups) =>
      groups.map((group) => {
        if (group.item.itemNumber !== item.itemNumber) return group;
        if (group.children.some((child) => !child.serialNumber)) return group;
        return {
          ...group,
          children: [...group.children, { ...item, serialNumber: null }],
        };
      }),
    );
  };

  const handleUpdateChild = (
    itemNumber: string,
    childIndex: number,
    field: string,
    value: any,
  ) => {
    setProductGroups((groups) =>
      groups.map((group) =>
        group.item.itemNumber === itemNumber
          ? {
              ...group,
              children: group.children.map((child, index) =>
                index === childIndex ? { ...child, [field]: value } : child,
              ),
            }
          : group,
      ),
    );
  };

  const fetchSerialNumbers = async (item: Product, keyword = "") => {
    setIsSerialLoading(true);
    setSerialError(null);
    try {
      const response = await getSerialNumbers(item.itemNumber, keyword);
      setSerialNumbers(response.data);
    } catch (error: any) {
      setSerialNumbers([]);
      setSerialError(error.message || "Failed to fetch serial numbers");
    } finally {
      setIsSerialLoading(false);
    }
  };

  const handleOpenSerialModal = (item: Product) => {
    setSerialModalItem(item);
    setSerialKeyword("");
    setSerialNumbers([]);
    setSerialError(null);
    setSelectedSerialNumbers(
      productGroups
        .find((group) => group.item.itemNumber === item.itemNumber)
        ?.children.filter((product) => product.serialNumber)
        .map((product) => product.serialNumber as string) || [],
    );
    void fetchSerialNumbers(item);
  };

  const handleSerialKeywordChange = (keyword: string) => {
    setSerialKeyword(keyword);
    if (serialTimerRef.current) {
      clearTimeout(serialTimerRef.current);
    }
    if (!serialModalItem) return;
    serialTimerRef.current = setTimeout(() => {
      void fetchSerialNumbers(serialModalItem, keyword.trim());
    }, 300);
  };

  const handleSerialSelection = (selected: SerialNumberItem[]) => {
    if (!serialModalItem) return;
    setProductGroups((groups) =>
      groups.map((group) => {
        if (group.item.itemNumber !== serialModalItem.itemNumber) return group;
        const existing = new Set(
          group.children
            .map((child) => child.serialNumber?.trim())
            .filter(Boolean),
        );
        const additions = selected
          .filter((serial) => {
            const value = serial.serialNumber.trim();
            if (!value || existing.has(value)) return false;
            existing.add(value);
            return true;
          })
          .map((serial) => ({
            ...serialModalItem,
            serialNumber: serial.serialNumber,
            warehouseId: serial.warehouseId,
            warehouseName: serial.warehouseName,
            quantity: serial.quantity,
            createDate: serial.createDate,
            expiredDate: serial.expiredDate,
            printQty: undefined,
          }));
        return { ...group, children: [...group.children, ...additions] };
      }),
    );
    setSerialModalItem(null);
    setSerialNumbers([]);
    setSelectedSerialNumbers([]);
  };

  const handleQuantityChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (Number(value) > 0 || value === "") {
      const newPrintSettings = { ...printSettings, [name]: value };
      setPrintSettings(newPrintSettings);
      await savePrintSettings(newPrintSettings);
    }
  };

  const onGenerateBarcode = async (
    itemNumber?: string,
    barcodeItem?: string,
  ) => {
    try {
      setIsLoading(true);
      const payload = { keyword: itemNumber, barcodeItem };
      const data = (await saveBarcode(payload)) as any;
      if (data?.data?.upcNo) {
        toast.success("Barcode generated successfully");
        setProductGroups((groups) =>
          groups.map((group) =>
            group.item.itemNumber === itemNumber
              ? {
                  ...group,
                  children: group.children.map((child) =>
                    child.itemNumber === itemNumber && !child.serialNumber
                      ? { ...child, itemBarcode: data.data.upcNo }
                      : child,
                  ),
                }
              : group,
          ),
        );
      } else {
        toast.error("Failed to generate barcode");
      }
    } catch (err) {
      toast.error("Failed to generate barcode");
    } finally {
      setIsLoading(false);
    }
  };

  const listProducts = productGroups.flatMap((group) => group.children);
  const listWithBarcode = listProducts.filter(
    (item) => getProductBarcode(item),
  );

  return (
    <Layout breadcrumbTitle="Print">
      <div className="w-full bg-white p-6 h-fit rounded-lg shadow-md">
        <div className="p-8">
          <h1 className="text-2xl font-bold mb-4">Search Product</h1>
          <div className="flex flex-col gap-3 mb-4 relative w-full sm:flex-row sm:items-end">
            <div className="relative flex-1">
              <label
                htmlFor="product-search"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Search Item
              </label>
              <input
                id="product-search"
                type="text"
                className="h-11 border border-gray-300 bg-white px-3 rounded-lg w-full shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder="Enter Product Name or SKU"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              {showSuggestions && suggestions.length > 0 && (
                <ul className="absolute left-0 right-0 bg-white border rounded-lg shadow-md max-h-60 overflow-y-auto z-10 mt-1">
                  {suggestions.map((item) => (
                    <li
                      key={`${item.itemNumber}-${item.serialNumber ?? "base"}`}
                      onClick={() => handleSelectSuggestion(item)}
                      className="p-2 cursor-pointer hover:bg-gray-100"
                    >
                      {item.itemName} ({item.itemNumber})
                      {item.serialNumber ? ` - ${item.serialNumber}` : ""}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="w-full sm:w-64">
              <label
                htmlFor="price-category"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Price Category
              </label>
              <div className="relative">
                <select
                  id="price-category"
                  aria-label="Price Category"
                  value={selectedPriceCategory}
                  onChange={(event) => setSelectedPriceCategory(event.target.value)}
                  className="h-11 appearance-none border border-gray-300 bg-gray-50 px-3 pr-10 rounded-lg w-full shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                >
                  <option value="">No adjustment</option>
                  {priceCategories.map((category) => (
                    <option key={category.name} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <svg
                  aria-hidden="true"
                  className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          {isLoading && (
            <div className="flex justify-center items-center mt-4">
              <span className="loader-clock"></span>
            </div>
          )}

          {productGroups.length > 0 && (
            <div className="border p-4 rounded shadow-md">
              <GroupedDetailProduct
                groups={productGroups}
                onAddUpc={handleAddUpc}
                onSelectSerialNumber={handleOpenSerialModal}
                onGenerateBarcode={(product) => {
                  setModalGenerate(true);
                  setProduct(product);
                }}
                isLoading={isLoading}
                onRemoveChild={handleRemoveChild}
                onUpdateChild={handleUpdateChild}
              />
              {listWithBarcode.length > 0 && (
                <>
                  <PrintSettings
                    printSettings={printSettings}
                    fetchPrintSettings={fetchPrintSettings}
                  />
                  <PrintPreview
                    settings={printSettings}
                    handleChange={handleChange}
                    handleQuantityChange={handleQuantityChange}
                    lists={listProducts}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <ModalGenerateBarcode
        isOpen={modalGenerate}
        onClose={() => {
          setModalGenerate(false);
          setProduct(null);
        }}
        onGenerateBarcode={onGenerateBarcode}
        product={product}
      />
      {serialModalItem && (
        <SerialNumberModal
          item={serialModalItem}
          serialNumbers={serialNumbers}
          selectedSerialNumbers={selectedSerialNumbers}
          keyword={serialKeyword}
          isLoading={isSerialLoading}
          error={serialError}
          onKeywordChange={handleSerialKeywordChange}
          onClose={() => setSerialModalItem(null)}
          onConfirm={handleSerialSelection}
        />
      )}
    </Layout>
  );
};

export default Home;
